import Box from "@mui/material/Box"
import { useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback, useEffect, useMemo } from "react"
import { FormProvider } from "react-hook-form"

import { GoodsReceiptProvider } from "../../../contexts"
import { useGetGoodsReceipt, useGoodsReceiptDetailsTabs, useGoodsReceiptStatusRedirect } from "../../../hooks"
import { GoodReceiptActionType, GoodReceiptDialogType } from "../../../types"
import {
  EMPTY_DEFAULT_FORM_VALUES,
  useIncomingGoodsReceiptActions,
  useIncomingPurchaseOrdersForm,
  usePurchaseOrderSelectionDialog,
  useSoftReceiveGoodsReceiptHandler,
} from "../hooks"
import { calculateIncomingPurchaseOrderFormTotals } from "../utils"

import { SoftReceiveDialog } from "./SoftReceiveDialog"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import { PermissionActionType } from "@/constants/permissions"
import { type GoodsReceipt, GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"
import { useNavigationConfirmation } from "@/hooks/useNavigationConfirmation"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import {
  GoodsReceiptDeleteConfirmation,
  GoodsReceiptDetailsLayout,
  PurchaseOrderRemoveConfirmation,
  PurchaseOrderSelectionDialog,
} from "@/screens/WarehouseReceiving/components"
import { GoodsReceiptFileFormDialog } from "@/screens/WarehouseReceiving/components/GoodsReceiptUploads/GoodsReceiptFileFormDialog"
import { PurchaseOrderSelectionWithFormContainer } from "@/screens/WarehouseReceiving/components/PurchaseOrderSelectionDialog/PurchaseOrderSelectionWithFormContainer"
import { SoftReceiveSuccessDialog } from "@/screens/WarehouseReceiving/components/SoftReceiveSuccessDialog"

export const IncomingGoodsReceiptDetailsLayout: FC = () => {
  const { isTabletView, getBasicPathFor } = useAppView()
  const { goodsReceiptId } = useParams({
    from: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/incoming/$goodsReceiptId/_layout`,
  })

  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  const { goodsReceipt, refetch } = useGetGoodsReceipt(goodsReceiptId)
  useGoodsReceiptStatusRedirect({ goodsReceipt, expectedStatus: GoodsReceiptStatus.Incoming })
  const { tabs, activeTab } = useGoodsReceiptDetailsTabs({
    goodsReceiptId,
    baseRoute: "incoming",
  })

  useEffect(() => {
    if (isTabletView) {
      window.scrollTo(0, 0)
    }
  }, [isTabletView])

  const {
    methods,
    canSubmit,
    isCancelDialogOpen,
    isResettingRef,
    handleCancel,
    handleCloseCancelConfirmation,
    handleConfirmCancel,
    handleSave,
    handleReset,
  } = useIncomingPurchaseOrdersForm({
    goodsReceipt: goodsReceipt ?? null,
    onSuccess: refetch,
  })

  const { watch } = methods
  const formValues = watch()

  const totals = useMemo(() => {
    const lineFormValues = formValues.purchaseOrders.flatMap((purchaseOrder) => purchaseOrder.lines)
    return calculateIncomingPurchaseOrderFormTotals(lineFormValues)
  }, [formValues])

  const existingOrderIds = useMemo(() => {
    return goodsReceipt?.orders?.map((order) => order.orderId) || []
  }, [goodsReceipt?.orders])

  const goodsReceiptBasePath = `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/incoming/${goodsReceiptId}`

  const {
    isConfirmationOpen: isNavigationConfirmationOpen,
    handleConfirmNavigation,
    handleCancelNavigation,
  } = useNavigationConfirmation({
    shouldBlock: canSubmit,
    allowedPath: goodsReceiptBasePath,
    onConfirmNavigation: () => {
      handleReset(EMPTY_DEFAULT_FORM_VALUES)
    },
  })

  const {
    searchQuery,
    inputValue,
    selectedPurchaseOrders,
    purchaseOrders,
    isFetching,
    handleSearch,
    handleInputChange,
    handleSelect,
    reset,
  } = usePurchaseOrderSelectionDialog(existingOrderIds)

  const {
    actionState,
    purchaseOrderToRemove,
    handleDialogClose,
    handleFormDialogSubmit,
    handleEditGoodsReceipt,
    handleDeleteGoodsReceipt,
    handleGoodsReceiptDeleteSuccess,
    handleAddPurchaseOrder,
    handleConfirmAddPurchaseOrder,
    handleRemovePurchaseOrder,
    handlePurchaseOrderRemoveClose,
    handlePurchaseOrderRemoveSuccess,
    handleSoftReceive,
    handleUploadFiles,
    handleSoftReceiveSuccess,
    handleSoftReceiveSuccessSubmit,
  } = useIncomingGoodsReceiptActions({
    goodsReceipt,
    selectedPurchaseOrders,
    formValues: formValues,
    hasUnsavedChanges: canSubmit,
    onRefetch: refetch,
    onSave: handleSave,
  })

  const { isReceiving, handleSoftReceiveSubmit } = useSoftReceiveGoodsReceiptHandler({
    goodsReceipt,
    onRefetch: refetch,
    onSoftReceiveSuccess: handleSoftReceiveSuccess,
  })

  const handleDialogCloseWithReset = useCallback(() => {
    reset()
    handleDialogClose()
  }, [reset, handleDialogClose])

  const handleRefreshData = useCallback(() => {
    refetch()
  }, [refetch])

  if (!goodsReceipt) {
    return null
  }

  const palletsCount = goodsReceipt.orders?.length ? totals.palletsCount : (goodsReceipt.estimatedPalletsCount ?? null)

  const headerActions = canMaintain ? (
    <Box className='flex items-center gap-2'>
      {!isTabletView && (
        <>
          <Button appearance='outlined' onClick={handleCancel} data-testid='cancel-button'>
            Cancel
          </Button>
          <Button appearance='outlined' disabled={!canSubmit} onClick={handleSave} data-testid='save-button'>
            Save
          </Button>
        </>
      )}

      <Button onClick={handleSoftReceive} data-testid='soft-receive-button'>
        Soft Receive
      </Button>
    </Box>
  ) : undefined

  return (
    <GoodsReceiptProvider
      value={{
        goodsReceipt,
        isResettingRef,
        onAddPurchaseOrder: handleAddPurchaseOrder,
        onRemovePurchaseOrder: handleRemovePurchaseOrder,
        onEdit: handleEditGoodsReceipt,
        onDelete: handleDeleteGoodsReceipt,
        onUpload: handleUploadFiles,
      }}
    >
      <FormProvider {...methods}>
        <GoodsReceiptDetailsLayout
          goodsReceipt={goodsReceipt}
          tabs={tabs}
          activeTab={activeTab}
          backToPath={`${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/incoming`}
          headerActions={headerActions}
          dataTestId='goods-receipt-details'
          palletsCount={palletsCount}
        />
      </FormProvider>

      {isCancelDialogOpen && <CancelDialog onClose={handleCloseCancelConfirmation} onConfirm={handleConfirmCancel} />}

      {isNavigationConfirmationOpen && (
        <CancelDialog onClose={handleCancelNavigation} onConfirm={handleConfirmNavigation} />
      )}

      {actionState?.type === GoodReceiptActionType.SoftReceive && (
        <SoftReceiveDialog
          onClose={handleDialogClose}
          onSubmit={handleSoftReceiveSubmit}
          estimatedPalletsCount={palletsCount}
          isLoading={isReceiving}
        />
      )}

      {actionState?.type === GoodReceiptActionType.SoftReceiveSuccess && (
        <SoftReceiveSuccessDialog
          onSoftReceiveSuccessSubmit={handleSoftReceiveSuccessSubmit}
          goodsReceipt={goodsReceipt}
        />
      )}

      {actionState?.type === GoodReceiptActionType.Form && (
        <PurchaseOrderSelectionWithFormContainer
          onClose={handleDialogClose}
          onSubmit={handleFormDialogSubmit}
          {...(actionState.goodsReceipt && {
            dialogType: GoodReceiptDialogType.GoodsReceipt,
            goodsReceipt: actionState.goodsReceipt,
          })}
        />
      )}

      {actionState?.type === GoodReceiptActionType.Delete && (
        <GoodsReceiptDeleteConfirmation
          goodsReceipt={
            (actionState as { type: GoodReceiptActionType.Delete; goodsReceipt: GoodsReceipt }).goodsReceipt
          }
          onClose={handleDialogClose}
          onSuccess={handleGoodsReceiptDeleteSuccess}
        />
      )}

      {purchaseOrderToRemove !== null && goodsReceipt && (
        <PurchaseOrderRemoveConfirmation
          goodsReceipt={goodsReceipt}
          purchaseOrderId={purchaseOrderToRemove}
          onClose={handlePurchaseOrderRemoveClose}
          onSuccess={handlePurchaseOrderRemoveSuccess}
          successMessage='Purchase Order successfully removed.'
        />
      )}

      {actionState?.type === GoodReceiptActionType.AddPurchaseOrder && (
        <PurchaseOrderSelectionDialog
          searchQuery={searchQuery}
          inputValue={inputValue}
          purchaseOrders={purchaseOrders}
          isFetching={isFetching}
          selectedPurchaseOrders={selectedPurchaseOrders}
          goodsReceipt={goodsReceipt}
          onSearch={handleSearch}
          onInputChange={handleInputChange}
          onSelect={handleSelect}
          onClose={handleDialogCloseWithReset}
          onContinue={(withPurchaseOrder) => {
            if (withPurchaseOrder) {
              reset()
              handleConfirmAddPurchaseOrder()
            } else {
              handleDialogCloseWithReset()
            }
          }}
          isStandalone
        />
      )}

      {actionState?.type === GoodReceiptActionType.FileUpload && (
        <GoodsReceiptFileFormDialog
          onClose={handleDialogClose}
          onRefresh={handleRefreshData}
          goodsReceiptId={goodsReceiptId}
        />
      )}
    </GoodsReceiptProvider>
  )
}
